import bikeCustomizationApi from '@/familyDetailsPage/api/bike-customization-api.js';
import {cloneRaw} from '@/utils/util';
import storefrontInstance from '@/api/instances/storefront';

export const GROUP_IDS = Object.freeze({
  THEME: 9,
  BODY_COLOR: 99,
});

export const SUBMISSION_STATUS = Object.freeze({
  SENT_TO_RETAILER: 'Sent',
  ORDERED: 'Ordered',
});

export const SUMMARY_DIALOG_VIEWS = Object.freeze({
  BUILD_SUMMARY: 'BUILD_SUMMARY',
  SUBMIT_TO_RETAILER: 'SUBMIT_TO_RETAILER',
  SAVE_BUILD: 'SAVE_BUILD',
  SHARE_EMAIL: 'SHARE_EMAIL',
});

export const ActionTypes = Object.freeze({
  FETCH_STARTING_CUSTOMIZATION_OPTIONS: 'fetchStartingCustomizationOptions',
  FETCH_COMPONENTS: 'fetchComponents',
  UPDATE_SELECTION: 'updateSelection',
  SAVE_CONFIG: 'saveConfig',
  FETCH_SAVED_SELECTIONS: 'fetchSavedSelections',
  FETCH_BIKE_CONFIGURATION: 'fetchBikeConfiguration',
  FETCH_THEMES: 'fetchThemes',
  FETCH_INSPIRATIONS: 'fetchInspirations',
  SUBMIT_TO_RETAILER: 'submitToRetailer',
});

export const MutationTypes = Object.freeze({
  SET_BUILD_ID: 'setBuildId',
  SET_SELECTIONS: 'setSelections',
  SET_BIKE_DATA: 'setBikeData',
  SET_IS_CUSTOMIZATION_DRAWER_OPEN: 'setIsCustomizationDrawerOpen',
  SET_CUSTOMIZE_DISCARD_DIALOG: 'setCustomizeDiscardDialog',
  SET_IS_CUSTOMIZATION_SHARE_EMAIL_DIALOG_OPEN: 'setIsCustomizationShareEmailDialogOpen',
  SET_IS_MODEL_CHANGE: 'setIsModelChange',
  SET_IS_SAVE_DIALOG_VISIBLE: 'setIsSaveDialogVisible',
  SET_IS_DIALOG_OPEN: 'setIsDialogOpen',
  SET_BIKE_DATA_CONFIGURATION_DESCRIPTION: 'setBikeDataConfigurationDescription',
  SET_BIKE_DATA_CONFIGURATION_CODE: 'setBikeDataConfigurationCode',
  SET_BIKE_DATA_P1_CONFIG_STATUS: 'setBikeDataP1ConfigStatus',
  SET_HAS_QUEUED_SAVE_ACTION: 'setHasQueuedSaveAction',
  SET_CACHED_BUILD_DATA: 'setCachedBuildData',
  CLEAR_CACHED_BUILD_DATA: 'clearCachedBuildData',
  FETCH_CACHED_BUILD_DATA: 'fetchCachedBuildData',
  SET_COMPONENT_OPTIONS: 'setComponentOptions',
  SET_THEME_OPTIONS: 'setThemeOptions',
  SET_INSPIRATIONS: 'setInspirations',
  SET_QUEUED_SHARE_ACTION: 'setQueuedShareAction',
  CLEAR_QUEUED_SHARE_ACTION: 'clearQueuedShareAction',
  SET_HAS_SAVED_BUILD_AND_UPDATED_ROUTE: 'setHasSavedBuildAndUpdatedRoute',
  SET_PAINT_SCHEME_OPTIONS: 'setPaintSchemeOptions',
  SET_HAS_CHANGED_SELECTIONS: 'setHasChangedSelections',
  SET_INCOMPATIBLE_SELECTION_ERROR_TOAST: 'setIncompatibleSelectionErrorToast',
  SET_IS_SUBMIT_TO_RETAILER_DIALOG_OPEN: 'setIsSubmitToRetailerDialogOpen',
  SET_HAS_QUEUED_SUBMIT_TO_RETAILER_ACTION: 'setHasQueuedSubmitToRetailerAction',
  SET_IS_CUSTOMIZATION_SUMMARY_DIALOG_OPEN: 'setIsCustomizationSummaryDialogOpen',
  SET_INSPIRATION_PRICES: 'setInspirationPrices',
  SET_SUMMARY_DIALOG_VIEW: 'setSummaryDialogView',
  SET_REOPEN_PREVIOUSLY_OPEN_DIALOG: 'setReopenPreviouslyOpenDialog',
  SET_IS_PROCESSING_CUSTOMIZATION_ACTION: 'setIsProcessingCustomizationAction',
  SET_IS_SAVED_BY_USER: 'setIsSavedByUser',
  SET_HAS_ERROR_LOADING_SAVED_BUILD: 'setHasErrorLoadingSavedBuild',
});

const removeUnusedDataFromResponse = (response, keys) => {
  if (!response) return;
  if (!keys.length || !Array.isArray(keys)) {
    console.error('Invalid keys provided for removal');
    return;
  }

  keys.forEach((key) => {
    delete response[key];
  });
};

const filterForUniqueThemes = (arr, key) => {
  // only keep unique marketing theme groups (i.e. ICON)
  return [...new Map(arr.map((item) => [item[key], item])).values()];
};

const bikeCustomization = {
  namespaced: true,
  state: {
    buildId: '',
    bikeData: {},
    isCustomizationDrawerOpen: false,
    isCustomizeDiscardDialogOpen: false,
    isCustomizationShareEmailDialogOpen: false,
    isCustomizationSummaryDialogOpen: false,
    isModelChange: false,
    isSaveDialogVisible: false,
    isDialogOpen: false,
    hasQueuedSaveAction: false,
    cachedBuildId: '',
    cachedSelections: [],
    cachedThemeOptions: [],
    cachedComponentOptions: [],
    cachedPaintSchemeOptions: [],
    componentOptions: [],
    themeOptions: [],
    paintSchemeOptions: [],
    queuedShareAction: '',
    hasSavedBuildAndUpdatedRoute: false,
    isSubmitToRetailerDialogOpen: false,
    hasQueuedSubmitToRetailerAction: false,
    hasChangedSelections: false,
    incompatibleSelectionErrorToast: '',
    summaryDialogView: SUMMARY_DIALOG_VIEWS.BUILD_SUMMARY,
    reopenPreviouslyOpenDialog: false,
    isProcessingCustomizationAction: false,
    isSavedByUser: false,
    hasErrorLoadingSavedBuild: false,
  },
  getters: {
    customizationImageLayers(state) {
      return state.bikeData?.imageLayers;
    },
    isSavedBuild(state) {
      return !!(state.bikeData?.code && state.bikeData?.description);
    },
    hasBikeBeenSubmittedToRetailer(state) {
      return state.bikeData?.p1ConfigStatus === SUBMISSION_STATUS.SENT_TO_RETAILER;
    },
    isBikeEditable(state) {
      return !(
        state.bikeData?.p1ConfigStatus === SUBMISSION_STATUS.SENT_TO_RETAILER ||
        state.bikeData?.p1ConfigStatus === SUBMISSION_STATUS.ORDERED
      );
    },
    selections(state) {
      return state.bikeData?.selections;
    },
    colorCategories(state) {
      return state.bikeData?.colorCategories;
    },
    selectedInspirationId(state) {
      return state.bikeData?.selections?.find((selection) => selection.groupId === 9)?.choiceId;
    },
    selectedInspiration(state, getters) {
      const allInspirations = state.themeOptions?.flatMap((theme) => theme.inspirations) || [];
      return allInspirations.find((inspiration) => inspiration.p1PresetThemeChoiceId === getters.selectedInspirationId);
    },
  },
  mutations: {
    [MutationTypes.SET_HAS_ERROR_LOADING_SAVED_BUILD](state, payload) {
      state.hasErrorLoadingSavedBuild = payload;
    },
    [MutationTypes.SET_BUILD_ID](state, payload) {
      state.buildId = payload;
    },
    [MutationTypes.SET_SELECTIONS](state, payload) {
      state.bikeData = {...state.bikeData, selections: payload};
    },
    [MutationTypes.SET_BIKE_DATA](state, payload) {
      state.bikeData = payload;
    },
    [MutationTypes.SET_IS_CUSTOMIZATION_DRAWER_OPEN](state, payload) {
      state.isCustomizationDrawerOpen = payload;
    },
    [MutationTypes.SET_CUSTOMIZE_DISCARD_DIALOG](state, payload) {
      state.isCustomizeDiscardDialogOpen = payload;
    },
    [MutationTypes.SET_IS_MODEL_CHANGE](state, payload) {
      state.isModelChange = payload;
    },
    [MutationTypes.SET_IS_SAVE_DIALOG_VISIBLE](state, payload) {
      state.isSaveDialogVisible = payload;
    },
    [MutationTypes.SET_IS_DIALOG_OPEN](state, payload) {
      state.isDialogOpen = payload;
    },
    [MutationTypes.SET_BIKE_DATA_CONFIGURATION_DESCRIPTION](state, payload) {
      state.bikeData.description = payload;
    },
    [MutationTypes.SET_BIKE_DATA_CONFIGURATION_CODE](state, payload) {
      state.bikeData.code = payload;
    },
    [MutationTypes.SET_BIKE_DATA_P1_CONFIG_STATUS](state, payload) {
      state.bikeData.p1ConfigStatus = payload;
    },
    [MutationTypes.SET_HAS_QUEUED_SAVE_ACTION](state, payload) {
      state.hasQueuedSaveAction = payload;
    },
    [MutationTypes.SET_CACHED_BUILD_DATA](state) {
      state.cachedBuildId = state.buildId;
      state.cachedSelections = state.bikeData.selections;
      state.cachedThemeOptions = state.themeOptions;
      state.cachedComponentOptions = state.componentOptions;
      state.cachedPaintSchemeOptions = state.paintSchemeOptions;
    },
    [MutationTypes.CLEAR_CACHED_BUILD_DATA](state) {
      state.cachedSelections = [];
      state.cachedBuildId = '';
      state.cachedThemeOptions = [];
      state.cachedComponentOptions = [];
      state.cachedPaintSchemeOptions = [];
    },
    [MutationTypes.FETCH_CACHED_BUILD_DATA](state) {
      state.buildId = state.cachedBuildId;
      state.bikeData.selections = state.cachedSelections;
      state.themeOptions = state.cachedThemeOptions;
      state.componentOptions = state.cachedComponentOptions;
      state.paintSchemeOptions = state.cachedPaintSchemeOptions;
    },
    [MutationTypes.SET_COMPONENT_OPTIONS](state, payload) {
      state.componentOptions = payload;
    },
    [MutationTypes.SET_IS_CUSTOMIZATION_SHARE_EMAIL_DIALOG_OPEN](state, payload) {
      state.isCustomizationShareEmailDialogOpen = payload;
    },
    [MutationTypes.SET_QUEUED_SHARE_ACTION](state, payload) {
      state.queuedShareAction = payload;
    },
    [MutationTypes.CLEAR_QUEUED_SHARE_ACTION](state) {
      state.queuedShareAction = '';
    },
    [MutationTypes.SET_THEME_OPTIONS](state, payload) {
      state.themeOptions = payload;
    },
    [MutationTypes.SET_INSPIRATIONS](state, {themeId, inspirations}) {
      state.themeOptions.find((theme) => theme.id === themeId).inspirations = inspirations;
    },
    [MutationTypes.SET_PAINT_SCHEME_OPTIONS](state, payload) {
      state.paintSchemeOptions = payload;
    },
    [MutationTypes.SET_HAS_SAVED_BUILD_AND_UPDATED_ROUTE](state, payload) {
      state.hasSavedBuildAndUpdatedRoute = payload;
    },
    [MutationTypes.SET_IS_SUBMIT_TO_RETAILER_DIALOG_OPEN](state, payload) {
      state.isSubmitToRetailerDialogOpen = payload;
    },
    [MutationTypes.SET_HAS_QUEUED_SUBMIT_TO_RETAILER_ACTION](state, payload) {
      state.hasQueuedSubmitToRetailerAction = payload;
    },
    [MutationTypes.SET_HAS_CHANGED_SELECTIONS](state, payload) {
      state.hasChangedSelections = payload;
    },
    [MutationTypes.SET_INCOMPATIBLE_SELECTION_ERROR_TOAST](state, payload) {
      state.incompatibleSelectionErrorToast = payload;
    },
    [MutationTypes.SET_IS_CUSTOMIZATION_SUMMARY_DIALOG_OPEN](state, payload) {
      state.isCustomizationSummaryDialogOpen = payload;
    },
    [MutationTypes.SET_INSPIRATION_PRICES](state, payload) {
      state.themeOptions.forEach((theme) => {
        theme.inspirations.forEach((inspiration) => {
          const price =
            payload.find((option) => option.id === inspiration.p1PresetThemeChoiceId)?.advertisedPrice || '';
          inspiration.advertisedPrice = price ? `+${price}` : '';
        });
      });
    },
    [MutationTypes.SET_SUMMARY_DIALOG_VIEW](state, payload) {
      state.summaryDialogView = payload;
    },
    [MutationTypes.SET_REOPEN_PREVIOUSLY_OPEN_DIALOG](state, payload) {
      state.reopenPreviouslyOpenDialog = payload;
    },
    [MutationTypes.SET_IS_PROCESSING_CUSTOMIZATION_ACTION](state, payload) {
      state.isProcessingCustomizationAction = payload;
    },
    [MutationTypes.SET_IS_SAVED_BY_USER](state, payload) {
      state.isSavedByUser = payload;
    },
  },
  actions: {
    async [ActionTypes.SUBMIT_TO_RETAILER]({commit, state}, payload) {
      const data = {
        code: state.bikeData?.code || null,
        packageId: state.bikeData?.id,
        description: state.bikeData?.description,
        selections: state.selections,
        p1ImageUrl: state.bikeData?.imageUrl,
        dealerId: payload.dealerId,
        customerAddress: {
          contactMethod: payload.contactMethod,
          email: payload.email,
          phone: payload.phone,
          name: payload.name,
        },
      };

      try {
        const response = await storefrontInstance.post('/project-one/api/configurations/submit', data, {
          toast: false,
        });

        if (response.status === 200 && response.data?.data) {
          commit(MutationTypes.SET_BIKE_DATA_P1_CONFIG_STATUS, response.data.data?.status);
          commit(MutationTypes.SET_HAS_SAVED_BUILD_AND_UPDATED_ROUTE, false);
          commit(MutationTypes.SET_HAS_QUEUED_SUBMIT_TO_RETAILER_ACTION, false);
          return response.data.data;
        }

        throw new Error('Unexpected response structure');
      } catch (error) {
        if (error.response?.status === 401) {
          if (state.isCustomizationSummaryDialogOpen) {
            commit(MutationTypes.SET_SUMMARY_DIALOG_VIEW, SUMMARY_DIALOG_VIEWS.SAVE_BUILD);
          } else {
            commit(MutationTypes.SET_IS_SUBMIT_TO_RETAILER_DIALOG_OPEN, false);
            commit(MutationTypes.SET_IS_SAVE_DIALOG_VISIBLE, true);
          }
          commit(MutationTypes.SET_HAS_QUEUED_SUBMIT_TO_RETAILER_ACTION, true);
        } else {
          throw error;
        }
      }
    },

    async [ActionTypes.FETCH_STARTING_CUSTOMIZATION_OPTIONS]({commit, dispatch}, packageIds) {
      const packageId = await bikeCustomizationApi.extractPackageId(packageIds);
      commit(MutationTypes.SET_BUILD_ID, packageId);
      dispatch(ActionTypes.FETCH_COMPONENTS);
      dispatch(ActionTypes.FETCH_THEMES);
    },
    //fetch and store base components and base price differences regardless of new or saved build
    async [ActionTypes.FETCH_COMPONENTS]({state, commit, rootGetters}) {
      const customProductStartingPoint = rootGetters['familyDetails/selectedModel']?.customProductStartingPoint;
      const selectionsUrl = `/project-one/api/packages/${customProductStartingPoint}/selections/components/`;
      try {
        let response;
        response = await bikeCustomizationApi.fetchModule(selectionsUrl);
        commit(MutationTypes.SET_HAS_SAVED_BUILD_AND_UPDATED_ROUTE, false);
        removeUnusedDataFromResponse(response?.data, ['navigation']);
        commit(MutationTypes.SET_COMPONENT_OPTIONS, response?.data?.groups);
        if (!state.buildId?.startsWith('P-')) {
          commit(MutationTypes.SET_BIKE_DATA, response?.data);
        }
      } catch (error) {
        console.error('Error fetching component options', error);
      }
    },

    async [ActionTypes.FETCH_SAVED_SELECTIONS]({commit, dispatch}, buildId) {
      if (!buildId) {
        console.error('No buildId provided');
        return;
      }
      try {
        const response = await bikeCustomizationApi.fetchSavedSelections(buildId);

        if (response?.status === 204) {
          throw new Error('Requested bike configuration no longer exists');
        }

        if (response?.status === 200) {
          const responseData = response.data.data;

          removeUnusedDataFromResponse(responseData, ['navigation', 'platform', 'packages']);

          await dispatch(ActionTypes.FETCH_STARTING_CUSTOMIZATION_OPTIONS, responseData.code);
          commit(MutationTypes.SET_BIKE_DATA, responseData);
          commit(MutationTypes.SET_HAS_CHANGED_SELECTIONS, true);
        }
      } catch (error) {
        commit(MutationTypes.SET_BIKE_DATA, null);
        console.error('Error fetching saved bike configuration', error);
        throw error;
      }
    },

    async [ActionTypes.FETCH_BIKE_CONFIGURATION]({commit}, buildId) {
      if (!buildId) {
        console.error('No buildId provided');
        return;
      }
      commit(MutationTypes.SET_BUILD_ID, buildId);
      const response = await bikeCustomizationApi.fetchBikeConfiguration(buildId);

      if (response?.status === 204) {
        throw new Error('Requested bike build does not exist');
      }

      if (response?.status === 200) {
        const responseData = response.data.data;

        if (responseData.p1ConfigSummaryUrl === '') {
          // Route to P1 experience if no FDP URL is provided in response
          window.location.href = `/project-one-builder/#/${responseData.code}/summary`;
          return;
        }
      }
    },

    async [ActionTypes.UPDATE_SELECTION]({commit, state}, {module, selections, presetPK, forceGet = false}) {
      try {
        const buildEndpointType = state.buildId?.startsWith('P-') ? 'configurations' : 'packages';
        let selectionsUrl = `/project-one/api/${buildEndpointType}/${state.buildId}/selections/${module}/`;

        if (presetPK) selectionsUrl += `?presetPK=${presetPK}`;

        let response;
        if (forceGet) {
          response = await bikeCustomizationApi.fetchModule(selectionsUrl);
        } else {
          const selectionsToUpdateMap = selections?.reduce((map, selection) => {
            map[selection.groupId] = selection;
            return map;
          }, {});

          const updatedSelections = cloneRaw(state.bikeData?.selections).map((selection) => {
            const updatedSelection = selectionsToUpdateMap[selection.groupId];
            if (updatedSelection && updatedSelection.choiceId !== selection.choiceId) {
              updatedSelection.oldChoiceId = selection.choiceId;
              return {...updatedSelection};
            }
            return selection;
          });

          response = await bikeCustomizationApi.buildModuleWithUserSelections({
            selectionsUrl,
            selections: updatedSelections,
          });

          if (response?.data?.invalidSelections?.length > 0) {
            const invalidSelections = response?.data?.invalidSelections;
            const payload = invalidSelections.map((selection) => selection.groupTitle).join(', ');
            commit(MutationTypes.SET_INCOMPATIBLE_SELECTION_ERROR_TOAST, payload);
          }
        }

        if (response?.data) {
          const responseData = response.data;

          if (module !== 'paint-scheme') {
            commit(MutationTypes.SET_HAS_CHANGED_SELECTIONS, true);
          }
          if (module === 'paint-scheme') {
            if (!state.paintSchemeOptions?.length) {
              commit(
                MutationTypes.SET_INSPIRATION_PRICES,
                responseData.groups?.find((group) => group.id === GROUP_IDS.THEME)?.choices || []
              );
            }
            commit(MutationTypes.SET_PAINT_SCHEME_OPTIONS, responseData.groups);
          }

          removeUnusedDataFromResponse(responseData, ['navigation']);
          commit(MutationTypes.SET_BIKE_DATA, responseData);
        }
      } catch (error) {
        console.error('Error updating selections', error);
        throw error;
      }
    },

    async [ActionTypes.SAVE_CONFIG]({state, commit, dispatch}, forceNew = false) {
      let data = {
        code: forceNew ? null : state.bikeData?.code,
        packageId: state.bikeData?.id,
        description: state.bikeData?.description,
        selections: state.bikeData?.selections,
        p1ImageUrl: state.bikeData?.imageUrl,
        packageDescription: state.bikeData?.modelName,
      };

      const method = state.bikeData?.code && !forceNew ? 'put' : 'post';
      try {
        const response = await bikeCustomizationApi.saveBikeConfig({method, data});

        if (response?.data) {
          commit(MutationTypes.SET_BUILD_ID, response.data.code);
          commit(MutationTypes.SET_BIKE_DATA_CONFIGURATION_CODE, response.data.code);
          commit(MutationTypes.SET_BIKE_DATA_CONFIGURATION_DESCRIPTION, response.data.description);
          commit(MutationTypes.SET_BIKE_DATA_P1_CONFIG_STATUS, response.data.status);
          if (!state.componentOptions.length) {
            await dispatch(ActionTypes.FETCH_STARTING_CUSTOMIZATION_OPTIONS, response.data.code);
          }

          commit(MutationTypes.SET_IS_SAVED_BY_USER, true);
          commit(MutationTypes.SET_HAS_SAVED_BUILD_AND_UPDATED_ROUTE, true);
          return response;
        }
      } catch (error) {
        if (error.response?.status === 401) {
          // We receive a 401 status when the user saving the bike is different from
          // the original creator of the bike. In that case we simply need to create
          // a new (copy) of the bike. So the save dialog is displayed.
          if (state.isCustomizationSummaryDialogOpen) {
            commit(MutationTypes.SET_SUMMARY_DIALOG_VIEW, SUMMARY_DIALOG_VIEWS.SAVE_BUILD);
          } else {
            commit(MutationTypes.SET_IS_SAVE_DIALOG_VISIBLE, true);
          }
        } else {
          console.error(error);
          throw new Error(error);
        }
      } finally {
        commit(MutationTypes.SET_HAS_QUEUED_SAVE_ACTION, false);
      }
    },
    //fetch and store base themes and price differences regardless of new or saved build
    async [ActionTypes.FETCH_THEMES]({rootGetters, commit, dispatch}) {
      const customProductStartingPoint = rootGetters['familyDetails/selectedModel']?.customProductStartingPoint;
      const themeUrl = `/project-one/api/packages/${customProductStartingPoint}/themes/`;
      try {
        const response = await bikeCustomizationApi.fetchThemes(themeUrl);
        let responseThemes = response?.data?.theme;
        if (responseThemes) {
          responseThemes = filterForUniqueThemes(responseThemes.choices, 'marketingThemeGroup');
        }
        commit(MutationTypes.SET_THEME_OPTIONS, responseThemes);
        // The initial /themes call returns inspirations from the default theme so
        // there is no need to call fetchInspirationsById on load
        const inspirations = response?.data?.inspirations.map((inspiration) => inspiration[0]) || [];
        commit(MutationTypes.SET_INSPIRATIONS, {themeId: responseThemes[0].id, inspirations});
        const otherThemes = responseThemes.filter((theme) => theme.marketingThemeGroup !== 'Select');
        if (!otherThemes?.length) return;
        try {
          await Promise.all(
            otherThemes.map((theme) =>
              dispatch(ActionTypes.FETCH_INSPIRATIONS, theme.id).catch((error) => {
                console.error(`Failed to fetch inspirations for theme ${theme.id}:`, error);
                return Promise.resolve();
              })
            )
          );
        } catch (error) {
          console.error('Error fetching multiple inspirations:', error);
        }
      } catch (error) {
        console.error('Error fetching themes', error);
      }
    },

    async [ActionTypes.FETCH_INSPIRATIONS]({rootGetters, commit}, themeId) {
      const customProductStartingPoint = rootGetters['familyDetails/selectedModel']?.customProductStartingPoint;
      const inspirationsUrl = `/project-one/api/packages/${customProductStartingPoint}/themes/${themeId}/`;

      try {
        const response = await bikeCustomizationApi.fetchInspirations(inspirationsUrl);
        const inspirations = response?.data?.inspirations.map((inspiration) => inspiration[0]) || [];
        commit(MutationTypes.SET_INSPIRATIONS, {themeId, inspirations});
      } catch (error) {
        console.error('Error fetching inspirations', error);
      }
    },
  },
};

export default bikeCustomization;
